"use client";

import { Core } from "@faire/design-tokens";
import type { IconId } from "@faire/web/slate/icons/__generated__";
import { Exclamation } from "@faire/web/slate/icons/foundation/Exclamation";
import { Typography } from "@faire/web/slate/Typography";
import { legacyComponentCls } from "@faire/web/slate/utils/tracking";
import { clsx } from "clsx";
import React, { type JSX } from "react";
import { toast as libToast, ToastPosition } from "react-toastify";
import type {
  ToastContent as LibToastContent,
  ToastOptions as LibToastOptions,
} from "react-toastify";

import { ToastClose } from "./ToastClose";
import { ToastIcon } from "./ToastIcon";

const TIMEOUT_DURATION = {
  short: 2000,
  medium: 4000,
  long: 6000,
};

export type ToastContent = React.ReactNode | (() => React.ReactNode);

type ToastTimeout = keyof typeof TIMEOUT_DURATION | number | false;

type ToastId = string;

export type ToastVariant = "primary" | "destructive";

export type ToastOptions<Data> = Partial<
  Pick<LibToastOptions<Data>, "updateId" | "data" | "style">
> & {
  variant?: ToastVariant;
  timeout?: ToastTimeout;
  toastId?: ToastId;
  onOpen?: OnToastOpen;
  onClose?: OnToastClose;
  position?: ToastPosition;
} & (
    | {
        variant?: "primary";
        icon?: JSX.Element | IconId;
      }
    | {
        variant: "destructive";
      }
  );

type UpdateOptions<Data> = ToastOptions<Data> & {
  render?: ToastContent;
};

type OnToastOpen = () => void;

type OnToastClose = (reason?: boolean | string) => void;

function getTimeoutDuration(timeout?: ToastTimeout): number | false {
  if (typeof timeout === "string") {
    return TIMEOUT_DURATION[timeout];
  }
  if (!timeout || timeout <= 0) {
    return false;
  }
  return timeout;
}

function getLibOptions<Data>(
  options?: ToastOptions<Data> | undefined
): LibToastOptions<Data> {
  const { onClose, onOpen, timeout, ...rest } = options ?? {};
  const autoClose = getTimeoutDuration(timeout);
  const dismissible = !autoClose;
  const isDestructive = options?.variant === "destructive";

  const closeButton = (
    <ToastClose
      closeToast={onClose}
      variant={isDestructive ? "tertiary" : "primary"}
    />
  );

  const icon = isDestructive ? (
    <Exclamation height="24px" width="24px" />
  ) : (
    options?.icon
  );
  return {
    ...rest,
    autoClose,
    icon: icon ? <ToastIcon icon={icon} /> : false,
    closeOnClick: false,
    pauseOnHover: false,
    isLoading: false,
    closeButton: dismissible ? closeButton : false,
    draggable: dismissible,
    className: clsx(
      {
        "Toastify__toast--dismissible": dismissible,
      },
      legacyComponentCls()
    ),
    onOpen,
    onClose,
    type: isDestructive ? "error" : "default",
    theme: isDestructive ? "colored" : "light",
  };
}

function getLibContent(content: ToastContent): LibToastContent {
  return typeof content === "string"
    ? () => (
        <Typography color={Core.text.inverse} className="w-full">
          {content}
        </Typography>
      )
    : content;
}

/**
 * @deprecated Use `Toast` from `@faire/slate/components/toast` instead. Since 2025-09-15
 *
 * Creates a new toast in ToastContainer.
 * @trackfunction
 *
 * @example
 * ```
 * import { Toast, createToast } from "@faire/web/slate-deprecated/Toast/Toast";
 *
 * createToast("This is a toast with string content.", { icon: "foundation:Truck" });
 * // => "9cf8vqy"
 *
 * Toast.create("This is a toast with string content.", { icon: "foundation:Truck" });
 * // => "tbfxltu"
 * ```
 *
 * @note Under the hood we're using `toast()` from https://fkhadra.github.io/react-toastify
 *
 * @param content What to render inside the toast body.
 * @param options The toast configuration options.
 * @returns The toast's id.
 */
export function createToast<Data extends object>(
  content: ToastContent,
  options?: ToastOptions<Data>
) {
  return libToast(getLibContent(content), getLibOptions(options)) as ToastId;
}

/**
 * @deprecated Use `Toast` from `@faire/slate/components/toast` instead. Since 2025-09-15
 *
 * Updates an active toast by id.
 *
 * @example
 * ```
 * import { Toast, updateToast } from "@faire/web/slate-deprecated/Toast/Toast";
 *
 * // update the text in the toast with id "9cf8vqy"
 * updateToast("9cf8vqy", { render: "This is the new toast string content." });
 *
 * // update the icon in the toast with id "tbfxltu"
 * Toast.update("tbfxltu", { icon: "foundation:MagicWand" });
 * ```
 *
 * @note Under the hood we're using `toast.update()` from https://fkhadra.github.io/react-toastify.
 *
 * @param toastdId The id of the toast being updated.
 * @param options The toast update options.
 */
export function updateToast<Data extends object>(
  toastId: ToastId,
  options?: UpdateOptions<Data>
) {
  const { render, ...rest } = options ?? ({} as UpdateOptions<Data>);
  libToast.update(toastId as number | string, {
    ...getLibOptions(rest),
    render: render !== undefined ? getLibContent(render) : undefined,
  });
}

/**
 * @deprecated Use `Toast` from `@faire/slate/components/toast` instead. Since 2025-09-15
 *
 * Dismisses an active toast by id if specified, otherwise dismisses all active toasts.
 *
 * @example
 * ```
 * import { Toast, dismissToast } from "@faire/web/slate-deprecated/Toast/Toast";
 *
 * // dismiss toast with id "9cf8vqy"
 * dismissToast("9cf8vqy");
 *
 * // dismiss all toasts
 * dismissToast();
 *
 * // dismiss toast with id "tbfxltu"
 * Toast.dismiss("tbfxltu");
 *
 * // dismiss all toasts
 * Toast.dismiss();
 * ```
 *
 * @note Under the hood we're using `toast.dismiss()` from https://fkhadra.github.io/react-toastify.
 *
 * @param toastdId The id of the toast being dismissed (optional).
 */
export function dismissToast(toastdId?: ToastId) {
  libToast.dismiss(toastdId);
}

/**
 * @deprecated Use `Toast` from `@faire/slate/components/toast` instead. Since 2025-09-15
 *
 * Checks to see if a toast is active by id.
 *
 * @example
 * ```
 * import { Toast, isToastActive } from "@faire/web/slate-deprecated/Toast/Toast";
 *
 * isToastActive("9cf8vqy");
 * Toast.isActive("tbfxltu");
 * ```
 *
 * @note Under the hood we're using `toast.isActive()` from https://fkhadra.github.io/react-toastify.
 *
 * @param toastdId The id of the toast being dismissed.
 * @returns Whether or not it is active.
 */
export function isToastActive(toastdId: ToastId): boolean {
  return libToast.isActive(toastdId);
}

/**
 * @deprecated Use `Toast` from `@faire/slate/components/toast` instead. Since 2025-09-15
 */
export namespace Toast {
  /**
   * @deprecated Use `Toast` from `@faire/slate/components/toast` instead. Since 2025-09-15
   */
  export const create = createToast;
  /**
   * @deprecated Use `Toast` from `@faire/slate/components/toast` instead. Since 2025-09-15
   */
  export const update = updateToast;
  /**
   * @deprecated Use `Toast` from `@faire/slate/components/toast` instead. Since 2025-09-15
   */
  export const dismiss = dismissToast;
  /**
   * @deprecated Use `Toast` from `@faire/slate/components/toast` instead. Since 2025-09-15
   */
  export const isActive = isToastActive;
}
